import axios, { endpoints } from 'src/utils/axios';

/**
 * Facility-initiated session cancellation with automatic wallet refunds.
 *
 * Standalone (not part of useSessions) so the dialog can run from the list
 * and the details page without mounting the list-fetching hook. Amounts on the
 * wire are major units (pesos) with a `_minor` twin in centavos — never send
 * an amount: the backend derives every refund from payment records.
 */

const unwrap = (response, fallback) => {
  if (response?.data?.status !== 'success') {
    throw new Error(response?.data?.message || fallback);
  }
  return response.data.data;
};

/** GET /admin/sessions/:id/cancellation-preview */
export async function getSessionCancellationPreview(sessionId) {
  const response = await axios.get(endpoints.sessions.cancellationPreview(sessionId));
  return unwrap(response, 'Failed to load cancellation preview');
}

/** POST /admin/sessions/:id/cancel — `{ reason_code, note? }` */
export async function cancelSessionWithRefund(sessionId, { reason_code, note }) {
  const body = { reason_code };
  if (note) body.note = note;
  const response = await axios.post(endpoints.sessions.cancel(sessionId), body);
  return unwrap(response, 'Failed to cancel session');
}

export const SESSION_CANCEL_FAILED_MESSAGE =
  'Unable to cancel session. No refunds were processed. The session remains unchanged. Please try again.';

/** Prefer the backend's own message (e.g. "Cannot cancel a session that is ongoing."). */
export function cancellationErrorMessage(error) {
  const data = error?.response?.data;
  const fromServer = data?.message ?? data?.data?.message;
  if (Array.isArray(fromServer)) return fromServer.join(' ');
  if (typeof fromServer === 'string' && fromServer.trim()) return fromServer;
  if (typeof error?.message === 'string' && error.message.trim() && error.message !== 'Network Error') {
    return error.message;
  }
  return SESSION_CANCEL_FAILED_MESSAGE;
}
